import type { AxiosError, AxiosInstance } from 'axios'
import { HttpProblemError, ProblemSchema } from '@collector/shared'

/**
 * Turn server RFC 7807 responses into typed HttpProblemError instances, and
 * attach a minimal Problem even for transport-level errors so callers only
 * ever need to handle HttpProblemError.
 */
export function installErrorInterceptor(client: AxiosInstance): void {
  client.interceptors.response.use(
    (r) => r,
    (error: unknown) => {
      const axErr = error as AxiosError<unknown>

      if (axErr.response) {
        const parsed = ProblemSchema.safeParse(axErr.response.data)
        const problem = parsed.success
          ? parsed.data
          : {
              type: 'about:blank',
              title: axErr.response.statusText || 'HTTP error',
              status: axErr.response.status,
              detail: typeof axErr.response.data === 'string' ? axErr.response.data : axErr.message,
            }
        throw new HttpProblemError(problem)
      }

      // No response: DNS failure, timeout, abort, etc. Surface as a 0-status
      // Problem so callers can branch on `err.status === 0` for transport
      // issues but still handle one error type.
      throw new HttpProblemError({
        type: 'about:blank',
        title: 'Network error',
        status: 0,
        detail: axErr.message || 'request failed before reaching the server',
        code: axErr.code,
      })
    },
  )
}
